import React, { useState } from 'react';
import Navbar from '../components/layout/Navbar';
import MobileMenu from '../components/layout/MobileMenu';
import HeroSection from '../components/layout/HeroSection';
import ToolSelector from '../components/layout/ToolSelector';
import CGPACalculator from '../components/calculators/CGPACalculator';
import SemesterCalculator from '../components/calculators/SemesterCalculator';
import TheoryCalculator from '../components/calculators/TheoryCalculator';
import LabCalculator from '../components/calculators/LabCalculator';
import PercentageCalculator from '../components/calculators/PercentageCalculator';
import ToolHeader from '../components/common/ToolHeader';
import GradeScalePanel from '../components/common/GradeScalePanel';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Loader } from 'lucide-react';

const CalculatorLayout = () => {
  const [currentView, setCurrentView] = useState('home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  React.useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login', { replace: true });
      } else {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center">
        <Loader size={24} className="animate-spin text-indigo-600 mb-4" />
        <p className="text-slate-500 font-medium">Verifying Access...</p>
      </div>
    );
  }

  const toolTitles = {
    cgpa: { title: 'CGPA Calculator', description: 'Calculate your cumulative GPA across all semesters by entering your current CGPA, completed credit hours, and current semester details.' },
    semester: { title: 'Semester GPA Calculator', description: 'Calculate your semester GPA by entering individual subject grades and their credit hours.' },
    theory: { title: 'Theory Subject GPA', description: 'Calculate the GPA for a single theory course based on assignments, quizzes, midterm, and final exam marks.' },
    lab: { title: 'Lab Subject GPA', description: 'Calculate combined GPA for courses with both theory and lab components using weighted calculations.' },
    percentage: { title: 'Percentage Tools', description: 'Convert between marks and percentages in both directions for any assessment.' },
  };

  const handleNavigate = (view) => {
    // If view is 'campus', we navigate away to the main app
    if (view === 'campus') {
      navigate('/campus');
      return;
    }
    setCurrentView(view);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackHome = () => {
    setCurrentView('home');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
      <Navbar 
        onMenuToggle={() => setIsMobileMenuOpen(true)} 
        onHomeClick={handleBackHome}
        onNavigate={handleNavigate}
        currentView={currentView}
      />
      <MobileMenu 
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onNavigate={handleNavigate}
        currentView={currentView}
      />
      
      <main className="max-w-7xl mx-auto px-4 pt-32 pb-20">
        {currentView === 'home' ? (
          <>
            <HeroSection />
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 text-center md:text-left">
                Choose Your Calculator
              </h2>
              <p className="text-slate-600 dark:text-slate-400 text-center md:text-left mb-8">
                Select the tool that fits your needs
              </p>
            </div>
            <ToolSelector onSelectTool={handleNavigate} />
          </>
        ) : (
          <div className="animate-in fade-in duration-500">
            <ToolHeader 
              title={toolTitles[currentView]?.title} 
              description={toolTitles[currentView]?.description}
              onBack={handleBackHome}
            />
            
            {currentView === 'cgpa' && <CGPACalculator />}
            {currentView === 'semester' && <SemesterCalculator />}
            {currentView === 'theory' && <TheoryCalculator />}
            {currentView === 'lab' && <LabCalculator />}
            {currentView === 'percentage' && <PercentageCalculator />}
            
            <GradeScalePanel />
          </div>
        )}

        <footer className="mt-16 pt-8 border-t border-slate-200 dark:border-slate-800 text-center text-slate-500 dark:text-slate-400 text-sm">
          <p className="mb-2">
            <span className="font-semibold text-slate-700 dark:text-slate-300">CUCalc</span> - COMSATS University GPA Calculator
          </p>
          <p className="text-xs text-slate-400 dark:text-slate-500">
            Not affiliated with COMSATS University Islamabad. This is a personal project created to help students.<br/>
            © {new Date().getFullYear()} All rights reserved.
          </p>
        </footer>
      </main>
    </div>
  );
};

export default CalculatorLayout;
